import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { MatDialog } from '@angular/material/dialog';
import { catchError, combineLatest, map, Observable, of, OperatorFunction, throwError } from 'rxjs';
import {
  AccountMappingsResponse,
  AccountMappingsUpsertRequest,
  UserMappingsResponse,
  UserMappingsUpsertRequest
} from './mapping.model';
import { ApiErrorDialogComponent } from '../shared/api-error-dialog.component';
import { MappingPendingCheck } from './mapping-pending-check.model';
import { MappingValidationRequest } from './mapping-validation-request.model';
import { UsersService } from '../users/users.service';
import { CigpStaffService } from '../cigp-staff/cigp-staff.service';
import { User } from '../users/user.model';
import { CigpStaff } from '../cigp-staff/cigp-staff.model';

function enrichMappingPendingCheck(
  mappingPendingCheck: MappingPendingCheck,
  users: User[],
  cigpStaffs: CigpStaff[]
): MappingPendingCheck {
  return {
    ...mappingPendingCheck,
    cigpStaff: mappingPendingCheck.cigpStaffEmail
      ? cigpStaffs.find((cigpStaff) => cigpStaff.email === mappingPendingCheck.cigpStaffEmail) || null
      : null,
    client: mappingPendingCheck.clientEmail
      ? users.find((user) => user.email === mappingPendingCheck.clientEmail) || null
      : null,
    id: `${mappingPendingCheck.mappingType}_${mappingPendingCheck.id}`
  };
}

function enrichObservableMappingsPendingCheck$(
  usersService: UsersService,
  cigpStaffService: CigpStaffService
): OperatorFunction<MappingPendingCheck[], MappingPendingCheck[]> {
  return (source: Observable<MappingPendingCheck[]>) =>
    combineLatest([source, usersService.getUsersFromCache$(), cigpStaffService.getStaffsFromCache$()]).pipe(
      map(([releases, users, staffs]) => releases.map((release) => enrichMappingPendingCheck(release, users, staffs))),
      map((releases) => releases.filter((r) => r !== null) as MappingPendingCheck[])
    );
}

@Injectable({
  providedIn: 'root'
})
export class MappingsService {
  private apiEndpoint = `${environment.apiEndpoint}/api/mappings`;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private apiErrorDialog: MatDialog,
    private usersService: UsersService,
    private cigpStaffService: CigpStaffService
  ) {}

  public listMappingsPendingCheck$(): Observable<MappingPendingCheck[]> {
    return this.http.get<MappingPendingCheck[]>(`${this.apiEndpoint}/pending-check`).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        this.apiErrorDialog.open(ApiErrorDialogComponent, {
          width: '800px',
          data: error
        });
        return throwError(() => error);
      }),
      enrichObservableMappingsPendingCheck$(this.usersService, this.cigpStaffService)
    );
  }

  public countMappingsPendingCheck$(): Observable<number> {
    return this.http.get<MappingPendingCheck[]>(`${this.apiEndpoint}/pending-check`).pipe(
      map((mappings) => mappings.length),
      catchError((error: any) => {
        this.logger.error(error);
        return of(0);
      })
    );
  }

  public getUsersMappedAccountIds$(userType: string, userEmail: string | null): Observable<UserMappingsResponse[]> {
    return this.http
      .get<UserMappingsResponse[]>(`${this.apiEndpoint}/users/${userType}`, {
        params: this.computeUserQueryParams(userEmail)
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error(error);
          this.apiErrorDialog.open(ApiErrorDialogComponent, {
            width: '800px',
            data: error
          });
          return throwError(() => error);
        })
      );
  }

  public upsertUserMappings$(mappingsRequest: UserMappingsUpsertRequest): Observable<UserMappingsResponse> {
    return this.http.post<UserMappingsResponse>(`${this.apiEndpoint}/users`, mappingsRequest).pipe(
      catchError((error) => {
        this.logger.error(error);
        this.apiErrorDialog.open(ApiErrorDialogComponent, {
          width: '800px',
          data: error
        });
        return throwError(() => error);
      })
    );
  }

  public getAccountsMappedUserEmails$(
    userType: string,
    accountId: number | null
  ): Observable<AccountMappingsResponse[]> {
    return this.http
      .get<AccountMappingsResponse[]>(`${this.apiEndpoint}/accounts/${userType}`, {
        params: this.computeAccountQueryParams(accountId)
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error(error);
          this.apiErrorDialog.open(ApiErrorDialogComponent, {
            width: '800px',
            data: error
          });
          return throwError(() => error);
        })
      );
  }

  public upsertAccountMappings$(mappingsRequest: AccountMappingsUpsertRequest): Observable<AccountMappingsResponse> {
    return this.http.post<AccountMappingsResponse>(`${this.apiEndpoint}/accounts`, mappingsRequest).pipe(
      catchError((error) => {
        this.logger.error(error);
        this.apiErrorDialog.open(ApiErrorDialogComponent, {
          width: '800px',
          data: error
        });
        return throwError(() => error);
      })
    );
  }

  public validateMapping$(validationRequest: MappingValidationRequest): Observable<void> {
    return this.http.post<void>(`${this.apiEndpoint}/validate`, validationRequest).pipe(
      catchError((error) => {
        this.logger.error(error);
        this.apiErrorDialog.open(ApiErrorDialogComponent, {
          width: '800px',
          data: error
        });
        return throwError(() => error);
      })
    );
  }

  private computeUserQueryParams(userEmail: string | null): { [param: string]: string } {
    const params: { [param: string]: string } = {};

    if (userEmail) {
      params['userEmail'] = userEmail;
    }

    return params;
  }

  private computeAccountQueryParams(accountId: number | null): { [param: string]: string } {
    const params: { [param: string]: string } = {};

    if (accountId) {
      params['accountId'] = accountId.toString();
    }

    return params;
  }
}
